import React, { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import { useI18n } from '../i18n/index.jsx';
import { TutorialPlayer, fmtDuration, tutText } from './Tutorials.jsx';
import { PLACE_MAP } from './places.js';
import './tutorials.css';

// "▶ Watch how" on any page (migration 037). <TutorialSlot place="ws.rfq.list" />
// in a page header shows the tutorials an admin placed there (Admin > Aide,
// or "Mode tutoriels" right on the page): a button (a small menu when there
// are several) and / or a dismissible tip banner. The list of every place is
// tutorials/places.js. Super admins can switch on "Mode tutoriels" (🎬,
// bottom corner): every slot of the page then shows "+ Ajouter une vidéo ici".

const TutorialPicker = lazy(() => import('./TutorialPicker.jsx'));
const Ctx = createContext({ forPlace: () => [], editMode: false });
const MODE_KEY = 'pme_tutorial_mode';
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* private mode */ } },
};

export function TutorialsProvider({ children }) {
  const { user, ready } = useAuth();
  const [places, setPlaces] = useState({});
  const [editMode, setEditMode] = useState(false);
  const isSuper = !!(user?.role === 'admin' && user?.isSuperAdmin && !user?.impersonation);
  const userId = user?.id || null;
  const load = useCallback(() => {
    api(userId ? '/tutorials/placed' : '/tutorials/placed/public').then((r) => setPlaces(r?.places || {})).catch(() => setPlaces({}));
  }, [userId]);
  useEffect(() => { if (ready) load(); }, [ready, load]);
  useEffect(() => { setEditMode(isSuper && store.get(MODE_KEY) === '1'); }, [isSuper]);
  const toggleMode = useCallback(() => setEditMode((m) => { store.set(MODE_KEY, m ? null : '1'); return !m; }), []);
  // Played: "until watched" placements go away (here and on the server).
  const markWatched = useCallback((tut) => {
    if (!userId) return;
    api(`/tutorials/${tut.id}/watched`, { method: 'POST' }).catch(() => {});
    setPlaces((p) => Object.fromEntries(Object.entries(p).map(([k, list]) => [k, list.filter((x) => !(x.id === tut.id && x.audience === 'unwatched'))])));
  }, [userId]);
  const value = useMemo(() => ({
    forPlace: (keys) => {
      const seen = new Set();
      return keys.flatMap((k) => places[k] || []).filter((x) => !seen.has(x.id) && seen.add(x.id));
    },
    editMode, isSuper, toggleMode, reload: load, markWatched,
  }), [places, editMode, isSuper, toggleMode, load, markWatched]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {isSuper && <ModeToggle on={editMode} onToggle={toggleMode} />}
    </Ctx.Provider>
  );
}
export const useTutorialPlaces = () => useContext(Ctx);

function ModeToggle({ on, onToggle }) {
  const { t } = useI18n();
  return (
    <button type="button" className={`tut-mode-toggle${on ? ' is-on' : ''}`} onClick={onToggle} aria-pressed={on} title={t('tutPlaces.modeTitle')}>
      <span aria-hidden="true">🎬</span> {on ? t('tutPlaces.modeOn') : t('tutPlaces.mode')}
    </button>
  );
}

const dismissKey = (id, place) => `pme_tut_tip_${id}_${place}`;

export default function TutorialSlot({ place, className = '' }) {
  const { t, lang } = useI18n();
  const { forPlace, editMode, markWatched, reload } = useTutorialPlaces();
  const keys = useMemo(() => (Array.isArray(place) ? place : [place]).filter((k) => PLACE_MAP[k]), [place]);
  const list = forPlace(keys);
  const [open, setOpen] = useState(null);
  const [menu, setMenu] = useState(false);
  const [picking, setPicking] = useState(false);
  const [, bump] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target)) setMenu(false); };
    const esc = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [menu]);
  const buttons = list.filter((x) => x.style !== 'banner');
  const banners = list.filter((x) => x.style === 'banner' && !store.get(dismissKey(x.id, keys[0])));
  const closePlayer = useCallback(() => setOpen(null), []);
  if (!keys.length || (!list.length && !editMode)) return null;
  const play = (tut) => { setMenu(false); setOpen(tut); };
  const label = (tut) => (tut.duration ? t('help.watchHowDur', { d: fmtDuration(tut.duration, t) }) : t('help.watchHow'));
  return (
    <div className={`tut-slot${editMode ? ' is-editing' : ''} ${className}`} data-tut-place={keys.join(' ')}>
      {banners.map((x) => (
        <div key={`b${x.id}`} className="tut-banner" role="note">
          <span className="tut-banner-text">{t('tutPlaces.bannerLead')} <strong>{tutText(x, 'title', lang)}</strong></span>
          <button type="button" className="tut-watch" onClick={() => play(x)} aria-haspopup="dialog"><span className="tut-watch-icon" aria-hidden="true">▶</span>{label(x)}</button>
          <button type="button" className="tut-banner-x" onClick={() => { store.set(dismissKey(x.id, keys[0]), '1'); bump((n) => n + 1); }} aria-label={t('tutPlaces.dismiss')}>✕</button>
        </div>
      ))}
      {buttons.length === 1 && (
        <button type="button" className="tut-watch" onClick={() => play(buttons[0])} aria-haspopup="dialog" title={tutText(buttons[0], 'title', lang)}>
          <span className="tut-watch-icon" aria-hidden="true">▶</span>{label(buttons[0])}
        </button>
      )}
      {buttons.length > 1 && (
        <span className="tut-menu" ref={ref}>
          <button type="button" className="tut-watch" onClick={() => setMenu((m) => !m)} aria-haspopup="menu" aria-expanded={menu}>
            <span className="tut-watch-icon" aria-hidden="true">▶</span>{t('tutPlaces.watchMany', { n: buttons.length })} <span aria-hidden="true">▾</span>
          </button>
          {menu && (
            <span className="tut-menu-list" role="menu">
              {buttons.map((x) => (
                <button key={x.id} type="button" role="menuitem" onClick={() => play(x)}>
                  <span>{tutText(x, 'title', lang)}</span>{x.duration > 0 && <span className="muted small">{fmtDuration(x.duration, t)}</span>}
                </button>
              ))}
            </span>
          )}
        </span>
      )}
      {editMode && <button type="button" className="tut-slot-add" onClick={() => setPicking(true)}>+ {t('tutPlaces.addHere')}</button>}
      {open && <TutorialPlayer tut={open} onClose={closePlayer} onPlay={() => markWatched(open)} />}
      {picking && (
        <Suspense fallback={null}>
          <TutorialPicker places={keys} onClose={() => setPicking(false)} onSaved={() => { setPicking(false); reload(); }} />
        </Suspense>
      )}
    </div>
  );
}
